import axios from 'axios'

import { parseAuthenticationHeader } from 'helpers/Functions'
import { ILayoutItem } from 'types/GlobalDashboard'
import { TCustomFieldValue } from 'types/GlobalCustomFieldValue'
import {
  DashboardStatus,
  TDashboardGroupResponse
} from 'types/GlobalDashboardGroup'
import { TDashboardWidgetLayout } from 'types/GlobalWidget'
import { getWithRedirect } from 'utils/axios'
import {
  TCustomFieldValuePatch,
  TDashboardResponse,
  TPublicDashboardResponse,
  TPutLayout
} from './types'

const BASE_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:3990/'
    : process.env.REACT_APP_HOMEPAL_MDM_API_PROD

const URL = `${BASE_URL}v1/dashboards`

export interface DashboardPostBody {
  data: {
    template_id?: string
    dashboard_id?: string
    dashboard_group_id?: string
    folder_id?: string | null
  }
}
export interface DashboardPatchBody {
  data: {
    status?: DashboardStatus
    subtitle?: string
    title?: string
    api_access?: boolean
    is_public?: boolean
  }
}

export type LayoutUpdate = TDashboardWidgetLayout & {
  widget_id: string
}

export interface WidgetLayoutPatchBody {
  data: {
    layout: LayoutUpdate[]
  }
}

export async function getOne(id: string) {
  return getWithRedirect<TDashboardResponse>(`${URL}/${id}`)
}

export async function getOnePublic(id: string) {
  return getWithRedirect<TPublicDashboardResponse>(
    `${BASE_URL}v1/public/dashboards/${id}`,
    {}
  )
}

export async function getOneApiAccess(id: string) {
  return getWithRedirect<TPublicDashboardResponse>(
    `${BASE_URL}v1/api-access/dashboards/${id}`
  )
}

export async function create(body: DashboardPostBody) {
  return axios.post<{
    data: {
      dashboard: TDashboardResponse
      group: TDashboardGroupResponse
    }
  }>(URL, body, parseAuthenticationHeader())
}

export async function move(
  id: string,
  body: { data: { dashboard_group_id: string } }
) {
  return axios.post<{ data: TDashboardResponse }>(
    `${URL}/${id}/move`,
    body,
    parseAuthenticationHeader()
  )
}

export async function destroy(id: string) {
  return axios.delete(`${URL}/${id}`, parseAuthenticationHeader())
}

export async function update(id: string, body: DashboardPatchBody) {
  return axios.patch<{ data: TDashboardResponse }>(
    `${URL}/${id}`,
    body,
    parseAuthenticationHeader()
  )
}

export async function updateLayout(id: string, data: TPutLayout) {
  return axios.put<{
    data: { available_space: ILayoutItem | null; layout: TPutLayout['layout'] }
  }>(`${URL}/${id}/update-layout`, { data }, parseAuthenticationHeader())
}

export async function patchCustomFieldValue(
  id: string,
  data: TCustomFieldValuePatch
) {
  return axios.patch<{ data: TCustomFieldValue }>(
    `${URL}/${id}/custom-field-values`,
    { data },
    parseAuthenticationHeader()
  )
}

export async function putThumbnail(id: string, thumbnail: Blob) {
  const formData = new FormData()

  formData.append('thumbnail', thumbnail)

  return axios.put(
    `${URL}/${id}/thumbnail`,
    formData,
    parseAuthenticationHeader()
  )
}
